import { z } from 'zod';
import type { CalendarUnit } from '../due.js';
import { DomainError } from '../errors.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';
import { DEFECT_DEPARTMENTS, type DefectDepartment } from './defect-report.js';
import { EQUIPMENT_CODE_MAX } from './equipment.js';
import {
  CALENDAR_UNITS,
  INTERVAL_BASES,
  type IntervalBasis,
  JOB_TYPES,
  type JobType,
  WORK_TYPES,
  type WorkType,
} from './jobs.js';

/**
 * Şirket PMS kütüphanesi (M15f; ADR 0063): sistem → ekipman kalemi → iş. Şirket verisidir, yalnız
 * bulutta durur; gemiye uygulama içeriğiyle iner (`library-apply.ts`). Excel biçimi ortağın
 * `Ship_PMS_Equipment_Components_and_Jobs.xlsx` dosyasıdır (§2). Saf modül: hata iletileri
 * koddur, arayüz çevirir.
 */

export const LIBRARY_SYSTEM_CODE_MAX = 8;
export const LIBRARY_ITEM_CODE_MAX = EQUIPMENT_CODE_MAX;
export const LIBRARY_JOB_CODE_MAX = 40;

/** İşin kütüphanedeki durumu: taslak (inceleme bekler) ya da onaylı (aralığı seçilmiş; §5). */
export const LIBRARY_JOB_STATUSES = ['draft', 'approved'] as const;
export type LibraryJobStatus = (typeof LIBRARY_JOB_STATUSES)[number];

/** Kalemin teknik inceleme durumu (Excel "Review Status"). */
export const LIBRARY_REVIEW_STATUSES = ['pending', 'reviewed'] as const;
export type LibraryReviewStatus = (typeof LIBRARY_REVIEW_STATUSES)[number];

/** Sorumlu departman: arıza formunun listesi (ADR 0060 §1); "Assigned Department" `null`. */
export const LIBRARY_DEPARTMENTS = DEFECT_DEPARTMENTS;
export type LibraryDepartment = DefectDepartment;

/** Kütüphane işinin aralığı: takvim ve/veya çalışma saati; durum noktası gemiye özeldir (K21). */
export interface LibraryInterval {
  calendar: { every: number; unit: CalendarUnit } | null;
  runningHours: number | null;
}

export const EMPTY_INTERVAL: LibraryInterval = { calendar: null, runningHours: null };

export interface LibrarySystemData {
  code: string;
  name: string;
  remarks: string | null;
}

export interface LibraryItemData {
  code: string;
  systemCode: string;
  subSystem: string | null;
  name: string;
  components: string | null;
  suggestedCritical: boolean;
  notes: string | null;
  maker: string | null;
  model: string | null;
  makerTaskRef: string | null;
  makerTask: string | null;
  makerInterval: string | null;
  manualRef: string | null;
  taskBasis: string | null;
  reviewStatus: LibraryReviewStatus;
}

export interface LibraryJobData {
  code: string;
  itemCode: string;
  title: string;
  workType: WorkType;
  jobType: JobType;
  interval: LibraryInterval;
  intervalBasis: IntervalBasis | null;
  planningBasis: string | null;
  makerRecommendation: string | null;
  makerInterval: string | null;
  manualRef: string | null;
  department: LibraryDepartment | null;
  critical: boolean;
  status: LibraryJobStatus;
}

/* ---------- Ortak metin kuralları ---------- */

const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');

/** Kodun karşılaştırma anahtarı: NFC, kırpılmış, büyük/küçük harf duyarsız (ADR 0063 §2). */
export const libraryKey = (code: string) => collapse(code).toLocaleLowerCase('en');

/** Kod: harf ya da rakamla başlar; harf, rakam, boşluk, nokta, tire, alt çizgi, eğik çizgi. */
const CODE_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N} ._\-/]*$/u;

export function isLibraryCode(code: string, max: number): boolean {
  return code.length >= 1 && code.length <= max && CODE_PATTERN.test(code);
}

/** Kodun türü: sistem, ekipman kalemi, iş. */
export type LibraryCodeKind = 'system' | 'item' | 'job';

const CODE_MAX: Readonly<Record<LibraryCodeKind, number>> = {
  system: LIBRARY_SYSTEM_CODE_MAX,
  item: LIBRARY_ITEM_CODE_MAX,
  job: LIBRARY_JOB_CODE_MAX,
};

/**
 * Elle girilen kod içe aktarmanın okuduğu biçime girer (ADR 0102): NFC, kırpılmış, tek boşluk;
 * tek haneli sistem kodu `0` ile (Excel'deki `1` → `01`). Böylece dışa aktarılan kod geri
 * okunduğunda aynı anahtara düşer ve "yalnız eksikler" kuralı onu ikinci kez yazmaz.
 */
export function normalizeLibraryCode(code: string, kind: LibraryCodeKind): string {
  const text = collapse(code);
  return kind === 'system' && /^\d$/.test(text) ? `0${text}` : text;
}

/**
 * Sıradaki kod (ADR 0102): `<önek><n>`; `n` aynı önekli ve sonu yalnız rakam olan kodların en
 * büyüğünden bir fazladır, `width` haneye sıfırla doldurulur (büyük/küçük harf duyarsız). Sınırı
 * ya da kod biçimini aşan öneri yoktur (`null`): kod elle girilir.
 */
function nextLibraryCode(
  prefix: string,
  codes: Iterable<string>,
  width: number,
  kind: LibraryCodeKind,
): string | null {
  const head = libraryKey(prefix);
  let top = 0;
  for (const code of codes) {
    const key = libraryKey(code);
    if (!key.startsWith(head)) continue;
    const rest = key.slice(head.length);
    if (/^\d+$/.test(rest)) top = Math.max(top, Number(rest));
  }
  const next = `${prefix}${String(top + 1).padStart(width, '0')}`;
  return isLibraryCode(next, CODE_MAX[kind]) ? next : null;
}

/** Yeni sistemin kodu: şablonun iki haneli düzeni (`01`…`22` → `23`). */
export const suggestLibrarySystemCode = (codes: Iterable<string>) =>
  nextLibraryCode('', codes, 2, 'system');

/** Yeni kalemin kodu: sistem kodu + üç haneli sıra (`01-016` → `01-017`). */
export const suggestLibraryItemCode = (systemCode: string, codes: Iterable<string>) =>
  nextLibraryCode(`${systemCode}-`, codes, 3, 'item');

/** Yeni işin kodu: `JOB-` + kalem kodu + iki haneli sıra (`JOB-01-001-04` → `JOB-01-001-05`). */
export const suggestLibraryJobCode = (itemCode: string, codes: Iterable<string>) =>
  nextLibraryCode(`JOB-${itemCode}-`, codes, 2, 'job');

/** Aralığın birimi ve gösterimi. */
export function sameLibraryInterval(a: LibraryInterval, b: LibraryInterval): boolean {
  return (
    (a.calendar?.every ?? null) === (b.calendar?.every ?? null) &&
    (a.calendar?.unit ?? null) === (b.calendar?.unit ?? null) &&
    (a.runningHours ?? null) === (b.runningHours ?? null)
  );
}

export const hasInterval = (interval: LibraryInterval) =>
  interval.calendar !== null || interval.runningHours !== null;

/* ---------- Excel eşlemeleri (§2) ---------- */

const WORD_INTERVALS: Readonly<Record<string, { every: number; unit: CalendarUnit }>> = {
  daily: { every: 1, unit: 'day' },
  weekly: { every: 1, unit: 'week' },
  fortnightly: { every: 2, unit: 'week' },
  biweekly: { every: 2, unit: 'week' },
  monthly: { every: 1, unit: 'month' },
  bimonthly: { every: 2, unit: 'month' },
  quarterly: { every: 3, unit: 'month' },
  halfyearly: { every: 6, unit: 'month' },
  semiannually: { every: 6, unit: 'month' },
  semiannual: { every: 6, unit: 'month' },
  sixmonthly: { every: 6, unit: 'month' },
  annually: { every: 1, unit: 'year' },
  annual: { every: 1, unit: 'year' },
  yearly: { every: 1, unit: 'year' },
  biennially: { every: 2, unit: 'year' },
  biennial: { every: 2, unit: 'year' },
};

const UNIT_WORDS: Readonly<Record<string, CalendarUnit | 'hours'>> = {
  d: 'day',
  day: 'day',
  days: 'day',
  w: 'week',
  wk: 'week',
  wks: 'week',
  week: 'week',
  weeks: 'week',
  m: 'month',
  mo: 'month',
  mth: 'month',
  mths: 'month',
  month: 'month',
  months: 'month',
  y: 'year',
  yr: 'year',
  yrs: 'year',
  year: 'year',
  years: 'year',
  h: 'hours',
  hr: 'hours',
  hrs: 'hours',
  hour: 'hours',
  hours: 'hours',
  rh: 'hours',
  runninghour: 'hours',
  runninghours: 'hours',
};

/** "8,000" / "8 000" / "8.000" (binlik ayraç) ve "1.5" (ondalık) sayıları. */
function numberOf(text: string): number | null {
  const thousands = /^\d{1,3}(?:[ ,.]\d{3})+$/.test(text) && !/^\d+\.\d{1,2}$/.test(text);
  const clean = thousands ? text.replace(/[ ,.]/g, '') : text;
  if (!/^\d+(?:\.\d+)?$/.test(clean)) return null;
  const value = Number(clean);
  return Number.isFinite(value) ? value : null;
}

/**
 * Excel'deki aralık metni (Suggested Interval): "3 months", "Monthly", "Quarterly", "500 h",
 * "8000 running hours", "12 months / 8000 h", "Every 2 years", "5-yearly". Takvim ve saat birlikte
 * olabilir (hangisi önce). Boş metin boş aralıktır; tanınmayan metin `null`.
 */
export function parseIntervalText(text: string | null | undefined): LibraryInterval | null {
  const source = (text ?? '').normalize('NFC').trim();
  if (source === '') return { ...EMPTY_INTERVAL };
  const parts = source
    .toLowerCase()
    .split(/\s*(?:\/|;|\||\+|\bor\b|\band\b|,(?!\d{3}\b))\s*/)
    .map((p) => p.trim())
    .filter((p) => p !== '');
  let calendar: LibraryInterval['calendar'] = null;
  let runningHours: number | null = null;
  for (const part of parts) {
    const compact = part.replace(/^every\s+/, '').replace(/[-\s]+/g, ' ');
    const word = WORD_INTERVALS[compact.replace(/\s+/g, '')];
    let found:
      | { kind: 'calendar'; every: number; unit: CalendarUnit }
      | { kind: 'hours'; value: number }
      | null = null;
    if (word) found = { kind: 'calendar', ...word };
    else {
      const match = /^(\d[\d ,.]*?)\s*([a-z][a-z ]*?)\.?$/.exec(compact);
      if (match) {
        const value = numberOf((match[1] as string).trim());
        const unitText = (match[2] as string).replace(/\s+/g, '');
        const yearlyLike = /^(daily|weekly|monthly|yearly)$/.exec(unitText)?.[1];
        const unit = yearlyLike
          ? ({ daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' } as const)[
              yearlyLike as 'daily'
            ]
          : UNIT_WORDS[unitText];
        if (value !== null && unit === 'hours' && value > 0) {
          found = { kind: 'hours', value };
        } else if (value !== null && unit && unit !== 'hours' && Number.isInteger(value)) {
          found = { kind: 'calendar', every: value, unit };
        }
      }
    }
    if (!found) return null;
    if (found.kind === 'hours') {
      if (runningHours !== null) return null;
      runningHours = found.value;
    } else {
      if (calendar !== null) return null;
      if (found.every < 1 || found.every > 1000) return null;
      calendar = { every: found.every, unit: found.unit };
    }
  }
  if (runningHours !== null && (runningHours > 1_000_000 || !Number.isInteger(runningHours * 10))) {
    return null;
  }
  return { calendar, runningHours };
}

/** Aralığın Excel metni (dışa aktarma): "3 months / 500 h". */
export function intervalText(interval: LibraryInterval): string {
  const parts: string[] = [];
  if (interval.calendar) {
    const { every, unit } = interval.calendar;
    parts.push(`${every} ${unit}${every === 1 ? '' : 's'}`);
  }
  if (interval.runningHours !== null) parts.push(`${interval.runningHours} h`);
  return parts.join(' / ');
}

/** Job Type eşlemesi: Excel'in üç türü ve Felix iş türleri (§2). */
export function libraryJobTypeOf(
  text: string | null | undefined,
): { workType: WorkType; jobType: JobType } | null {
  const key = collapse(text ?? '').toLowerCase();
  if (key === '') return null;
  if (key === 'maintenance' || key === 'preventive maintenance') {
    return { workType: 'Service', jobType: 'preventive' };
  }
  if (key === 'replacement') return { workType: 'Replace', jobType: 'preventive' };
  const workType = WORK_TYPES.find((w) => w.toLowerCase() === key);
  if (!workType) return null;
  const jobType: JobType =
    workType === 'Inspection' ? 'inspection' : workType === 'Test' ? 'test' : 'preventive';
  return { workType, jobType };
}

/** Excel'e yazılacak tür adı: Service/preventive → "Maintenance". */
export function libraryJobTypeLabel(job: Pick<LibraryJobData, 'workType' | 'jobType'>): string {
  return job.workType === 'Service' ? 'Maintenance' : job.workType;
}

const DEPARTMENT_ALIASES: Readonly<Record<string, LibraryDepartment>> = {
  eto: 'electrical',
  'electro technical': 'electrical',
  bridge: 'navigation',
  catering: 'galley',
  hotel: 'accommodation',
};

/**
 * Responsible Department: listedeki adlar ("Engine" → `engine`); "Assigned Department" ve boş
 * `null` (atanmamış). Tanınmayan `undefined`.
 */
export function libraryDepartmentOf(
  text: string | null | undefined,
): LibraryDepartment | null | undefined {
  const key = collapse(text ?? '').toLowerCase();
  if (key === '' || key === 'assigned department' || key === 'to be assigned') return null;
  const direct = LIBRARY_DEPARTMENTS.find((d) => d === key || `${d} department` === key);
  return direct ?? DEPARTMENT_ALIASES[key];
}

/** Evet/hayır hücresi: Yes/Y/True/1/X ve No/N/False/0/boş; tanınmayan `undefined`. */
export function flagOf(value: string | null | undefined): boolean | undefined {
  const key = collapse(value ?? '').toLowerCase();
  if (['yes', 'y', 'true', '1', 'x', 'evet'].includes(key)) return true;
  if (['', 'no', 'n', 'false', '0', 'hayır', 'hayir', '-'].includes(key)) return false;
  return undefined;
}

/** "Manufacturer / Model" tek sütun: ilk " / " ya da ", " ile ayrılır (§2). */
export function splitMakerModel(text: string | null): {
  maker: string | null;
  model: string | null;
} {
  if (text === null) return { maker: null, model: null };
  const match = /^(.+?)\s*(?:\s\/\s|,\s)\s*(.+)$/.exec(text);
  if (!match) return { maker: text, model: null };
  return { maker: (match[1] as string).trim(), model: (match[2] as string).trim() };
}

/** "Task Basis (Suggested)" → aralık dayanağı; "To be defined" ve tanınmayan `null`. */
/**
 * İşin dayanağının Excel etiketi: dışa aktarma işin dayanağını "Planning Basis" sütununa bu
 * etiketle yazar (ADR 0102); içe aktarma etiketin tam kendisini işin dayanağı sayar.
 */
export const LIBRARY_BASIS_LABELS: Readonly<Record<IntervalBasis, string>> = {
  maker: 'Manufacturer',
  class: 'Class',
  experience: 'Experience',
  company: 'Company',
};

/** Metin tam olarak bir dayanak etiketiyse o dayanak; değilse `null` (serbest planlama metni). */
export function libraryBasisLabelOf(text: string | null | undefined): IntervalBasis | null {
  const key = collapse(text ?? '').toLowerCase();
  const found = INTERVAL_BASES.find((basis) => LIBRARY_BASIS_LABELS[basis].toLowerCase() === key);
  return found ?? null;
}

export function intervalBasisOf(text: string | null | undefined): IntervalBasis | null {
  const key = collapse(text ?? '').toLowerCase();
  if (/^(maker|manufacturer|oem)\b/.test(key)) return 'maker';
  if (/^class\b/.test(key)) return 'class';
  if (/^experience\b/.test(key)) return 'experience';
  if (/^(company|sms)\b/.test(key)) return 'company';
  return null;
}

/** Job Status: "Draft – Review" ve boş taslak; "Approved", "Reviewed", "Active" onaylı. */
export function libraryJobStatusOf(text: string | null | undefined): LibraryJobStatus | undefined {
  const key = collapse(text ?? '').toLowerCase();
  if (key === '' || key.startsWith('draft')) return 'draft';
  if (['approved', 'reviewed', 'active'].includes(key)) return 'approved';
  return undefined;
}

export function libraryReviewStatusOf(
  text: string | null | undefined,
): LibraryReviewStatus | undefined {
  const key = collapse(text ?? '').toLowerCase();
  if (key === '' || key.startsWith('pending')) return 'pending';
  if (['reviewed', 'approved'].includes(key)) return 'reviewed';
  return undefined;
}

/* ---------- Ofiste düzenleme (§5) ---------- */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});

const text = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));

const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

const optionalLines = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) =>
      value
        .normalize('NFC')
        .split(/\r\n|\r|\n/)
        .map(collapse)
        .filter((line) => line !== '')
        .join('\n'),
    )
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

const flag = () =>
  z
    .boolean(typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? false);

const option = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values, typed('INVALID_OPTION'));

const closed = <T extends z.core.$ZodLooseShape>(shape: T) =>
  z.strictObject(shape, {
    error: (issue) =>
      issue.code === 'unrecognized_keys'
        ? 'UNRECOGNIZED_KEY'
        : missing(issue.input)
          ? 'REQUIRED'
          : 'INVALID_OBJECT',
  });

/** Uzunluk sınırları: Excel'den gelen ve ofiste düzenlenen alanlar aynı sınırla. */
export const LIBRARY_LIMITS = {
  systemName: 120,
  remarks: 500,
  subSystem: 120,
  itemName: 120,
  components: 500,
  notes: 2000,
  maker: 120,
  model: 120,
  makerText: 500,
  manualRef: 200,
  taskBasis: 200,
  jobTitle: 200,
  planningBasis: 200,
  makerRecommendation: 1000,
  makerInterval: 100,
} as const;

export const libraryIntervalSchema = closed({
  calendar: closed({
    every: z
      .number(typed('INVALID_NUMBER'))
      .int('INVALID_NUMBER')
      .min(1, 'OUT_OF_RANGE')
      .max(1000, 'OUT_OF_RANGE'),
    unit: option(CALENDAR_UNITS),
  })
    .nullish()
    .transform((value) => value ?? null),
  runningHours: z
    .number(typed('INVALID_NUMBER'))
    .positive('OUT_OF_RANGE')
    .max(1_000_000, 'OUT_OF_RANGE')
    .refine((value) => Number.isInteger(value * 10), 'INVALID_HOURS')
    .nullish()
    .transform((value) => value ?? null),
});

/** Kod alanı: içe aktarmanın biçimi (`normalizeLibraryCode`), sınır ve kod deseni. */
const codeField = (kind: LibraryCodeKind) =>
  z
    .string(typed('INVALID_CODE'))
    .transform((value) => normalizeLibraryCode(value, kind))
    .pipe(
      z
        .string()
        .min(1, 'REQUIRED')
        .refine((value) => isLibraryCode(value, CODE_MAX[kind]), 'INVALID_CODE'),
    );

/** Şirketin kendi sistemi (ADR 0102): kod ≤ 8, canlılar arasında tekil (yazma yolu denetler). */
export const librarySystemInputSchema = closed({
  code: codeField('system'),
  name: text(LIBRARY_LIMITS.systemName),
  remarks: optionalText(LIBRARY_LIMITS.remarks),
});
export type LibrarySystemInput = z.output<typeof librarySystemInputSchema>;

export function parseLibrarySystemInput(input: unknown): ParseResult<LibrarySystemInput> {
  const result = librarySystemInputSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Elle eklenen kalemin ve işin kodu (ADR 0102); geri kalan alanlar düzenleme şemasından. */
export function parseLibraryCode(code: unknown, kind: LibraryCodeKind): ParseResult<string> {
  const result = codeField(kind).safeParse(code);
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: toIssues(result.error).map((issue) => ({ ...issue, path: 'code' })),
      };
}

/** Kütüphane kalemi (ofiste düzenleme; kod ve sistem değişmez). */
export const libraryItemInputSchema = closed({
  subSystem: optionalText(LIBRARY_LIMITS.subSystem),
  name: text(LIBRARY_LIMITS.itemName),
  components: optionalText(LIBRARY_LIMITS.components),
  suggestedCritical: flag(),
  notes: optionalLines(LIBRARY_LIMITS.notes),
  maker: optionalText(LIBRARY_LIMITS.maker),
  model: optionalText(LIBRARY_LIMITS.model),
  makerTaskRef: optionalText(LIBRARY_LIMITS.makerText),
  makerTask: optionalText(LIBRARY_LIMITS.makerText),
  makerInterval: optionalText(LIBRARY_LIMITS.makerInterval),
  manualRef: optionalText(LIBRARY_LIMITS.manualRef),
  taskBasis: optionalText(LIBRARY_LIMITS.taskBasis),
  reviewStatus: option(LIBRARY_REVIEW_STATUSES),
});
export type LibraryItemInput = z.output<typeof libraryItemInputSchema>;

/**
 * Kütüphane işi (ofiste düzenleme; kod ve kalem değişmez). Onaylı iş aralık ve dayanak ister
 * (`LIBRARY_INTERVAL_REQUIRED`, ADR 0063 §5).
 */
export const libraryJobInputSchema = closed({
  title: text(LIBRARY_LIMITS.jobTitle),
  workType: option(WORK_TYPES),
  jobType: option(JOB_TYPES),
  interval: libraryIntervalSchema,
  intervalBasis: option(INTERVAL_BASES)
    .nullish()
    .transform((value) => value ?? null),
  planningBasis: optionalText(LIBRARY_LIMITS.planningBasis),
  makerRecommendation: optionalText(LIBRARY_LIMITS.makerRecommendation),
  makerInterval: optionalText(LIBRARY_LIMITS.makerInterval),
  manualRef: optionalText(LIBRARY_LIMITS.manualRef),
  department: option(LIBRARY_DEPARTMENTS)
    .nullish()
    .transform((value) => value ?? null),
  critical: flag(),
  status: option(LIBRARY_JOB_STATUSES),
}).superRefine((input, ctx) => {
  if (input.status !== 'approved') return;
  if (!hasInterval(input.interval)) {
    ctx.addIssue({ code: 'custom', message: 'LIBRARY_INTERVAL_REQUIRED', path: ['interval'] });
  }
  if (input.intervalBasis === null) {
    ctx.addIssue({
      code: 'custom',
      message: 'LIBRARY_INTERVAL_REQUIRED',
      path: ['intervalBasis'],
    });
  }
});
export type LibraryJobInput = z.output<typeof libraryJobInputSchema>;

export function parseLibraryItemInput(input: unknown): ParseResult<LibraryItemInput> {
  const result = libraryItemInputSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

export function parseLibraryJobInput(input: unknown): ParseResult<LibraryJobInput> {
  const result = libraryJobInputSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Kütüphane işinin onay kuralı (yazma yolu da denetler). */
export function assertLibraryJobApprovable(
  job: Pick<LibraryJobData, 'status' | 'interval' | 'intervalBasis'>,
): void {
  if (job.status !== 'approved') return;
  if (!hasInterval(job.interval) || job.intervalBasis === null) {
    throw new DomainError('LIBRARY_INTERVAL_REQUIRED');
  }
}
